import { css } from '@emotion/react';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { TextareaComponent } from '~/_shared/baseComponents/textarea/textarea.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { Trans } from '~/translations/Trans';

const ratedLabelStyle = css({
  fontSize: 18,
  fontWeight: 400,
});

const scoreStyle = (theme: Theme) => css({
  color: theme.textColors.info,
  fontWeight: 700,
});

const labelStyle = css({
  alignItems: 'center',
  display: 'flex',
  fontSize: 18,
  fontWeight: 300,
});

const optionalStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
});

const getDetailsLabel = (score: number, t: TranslationFnc) => {
  if (score <= 4) {
    return t('nps.details.label.detractor');
  }
  if (score <= 6) {
    return t('nps.details.label.neutral');
  }
  if (score <= 8) {
    return t('nps.details.label.satisfied');
  }
  return t('nps.details.label.promoter');
};

type NpsDetailsComponentProps = Readonly<{
  details: string;
  onDetailsChange: (value: string) => void;
  score: number;
}>;

export const NpsDetailsComponent = ({
  score, details, onDetailsChange,
}: NpsDetailsComponentProps) => {
  const [t] = useTranslation();
  const theme = useTheme();

  return (
    <Stack gap={12}>
      <span css={ratedLabelStyle}>
        <Trans
          components={{ score: <span css={scoreStyle} /> }}
          i18nKey="nps.details.ratedLabel"
          values={{ score }}
        />
      </span>
      <Stack gap={4}>
        <Inline
          css={labelStyle}
          gap={4}
        >
          {getDetailsLabel(score, t)}
          <span css={optionalStyle(theme)}>{t('(optional)')}</span>
        </Inline>
        <TextareaComponent
          onChange={onDetailsChange}
          placeholder={t('nps.details.placeholder')}
          value={details}
        />
      </Stack>
    </Stack>
  );
};
